// Runtime config injected via /config.js at container startup
const cfg = window.__RUNTIME_CONFIG__ || {} as RuntimeConfig

// Feature flags
export const ENABLE_WEARABLES: boolean = cfg.ENABLE_WEARABLES === 'true'
export const ENABLE_DARK_MODE: boolean = cfg.ENABLE_DARK_MODE === 'true'
export const ENABLE_SSE_CHAT: boolean = cfg.ENABLE_SSE_CHAT === 'true'
export const ENABLE_STREAM_CHAT: boolean = cfg.ENABLE_STREAM_CHAT === 'true'

// Base URLs
// TODO: Rename to match service names — API_BASE_URL → IDENTITY_BASE_URL,
// SYSTEM_BASE_URL → AIML_BASE_URL, ANALYTICS_BASE_URL → ANALYTICS_BASE_URL (already clear).
// Touches every service file — do as a separate PR.
export const API_BASE_URL: string = cfg.API_BASE_URL           // Identity service (identity.treatwise.ai)
export const ANALYTICS_BASE_URL: string = cfg.ANALYTICS_BASE_URL // Analytics service (analytics.treatwise.ai)
export const SYSTEM_BASE_URL: string = cfg.SYSTEM_BASE_URL     // AIML service (inferencing.treatwise.ai)

// OAuth Endpoints — Cognito authorize URLs with identity_provider hint
// Provider names must match Cognito IdP names in locals.tf:
// Google = "Google", Apple = "SignInWithApple", Microsoft = "Microsoft", GitHub = "GitHub"
export const OAUTH_ENDPOINTS: OAuthEndpoints = {
  GOOGLE_LOGIN: `${cfg.COGNITO_DOMAIN}/oauth2/authorize?response_type=code&client_id=${cfg.COGNITO_CLIENT_ID}&redirect_uri=${encodeURIComponent(cfg.COGNITO_REDIRECT_URI)}&identity_provider=Google`,

  APPLE_LOGIN: `${cfg.COGNITO_DOMAIN}/oauth2/authorize?response_type=code&client_id=${cfg.COGNITO_CLIENT_ID}&redirect_uri=${encodeURIComponent(cfg.COGNITO_REDIRECT_URI)}&identity_provider=SignInWithApple`,

  MICROSOFT_LOGIN: `${cfg.COGNITO_DOMAIN}/oauth2/authorize?response_type=code&client_id=${cfg.COGNITO_CLIENT_ID}&redirect_uri=${encodeURIComponent(cfg.COGNITO_REDIRECT_URI)}&identity_provider=Microsoft`,

  GITHUB_LOGIN: `${cfg.COGNITO_DOMAIN}/oauth2/authorize?response_type=code&client_id=${cfg.COGNITO_CLIENT_ID}&redirect_uri=${encodeURIComponent(cfg.COGNITO_REDIRECT_URI)}&identity_provider=GitHub`,

  LINKEDIN_LOGIN: `${cfg.COGNITO_DOMAIN}/oauth2/authorize?response_type=code&client_id=${cfg.COGNITO_CLIENT_ID}&redirect_uri=${encodeURIComponent(cfg.COGNITO_REDIRECT_URI)}&identity_provider=LinkedIn`,
}
interface AuthEndpoints {
  REGISTER: string
  LOGIN: string
  LOG_OUT: string
  VERIFY_ACCOUNT: string
  FORGOT_PASSWORD: string
  RESET_PASSWORD: string
  CHANGE_PASSWORD: string
  RESEND_CODE: string
  TOKEN: string
  OAUTH_CALLBACK: (code: string) => string
  MFA_SETUP: string
  MFA_VERIFY: string
  MFA_DISABLE: string
  MFA_STATUS: string
  MFA_CHALLENGE: string
  MFA_RECOVERY_GENERATE: string
  MFA_RECOVERY_VERIFY: string
  MFA_RECOVERY_STATUS: string
}
interface Analytics {
  PLATFORM_STATS: string
}
interface System {
  VERSION: string
}
interface Pulse {
  TODAY: string
}

interface Ahead {
  GET: string
  COMPLETE: (id: string) => string
  DISMISS: (id: string) => string
  RESTORE: (id: string) => string
  REFRESH: string
}

interface Handoff {
  GET: string
  UPDATE: string
  SHARE: string
  VIEW: (token: string) => string
  REVOKE: (token: string) => string
}

interface Chat {
  SUBMIT_CHAT: string
  PROMPT_SUGGESTIONS: string
  GET_CHAT_HISTORY: (sessionId: string) => string
  GET_USER_CONVERSATIONS: (
    page: number,
    pageSize: number,
    startDate?: string,
    endDate?: string
  ) => string
  CREATE_WALK: (sessionId: string) => string
  REVOKE_WALK: (token: string) => string
  VIEW_WALK: (token: string) => string
  STREAM_WALK: (token: string) => string
  CREATE_CLIP: (messageId: string) => string
  VIEW_CLIP: (token: string) => string
  REVOKE_CLIP: (token: string) => string
}

interface USER {
  UPDATE_PROFILE_PICTURE: string
  DELETE_PROFILE_PICTURE: string
  GET_PROFILE: string
  UPDATE_PROFILE: string
  SAVE_PROFILE: string
  DELETE_ALL_DATA: string
  MEDICAL_PROFILE: string
}

// Define types for OAuth endpoints
interface OAuthEndpoints {
  GOOGLE_LOGIN: string
  APPLE_LOGIN: string
  MICROSOFT_LOGIN: string
  GITHUB_LOGIN: string
  LINKEDIN_LOGIN: string
}

// Auth Endpoints
export const AUTH_ENDPOINTS: AuthEndpoints = {
  REGISTER: '/auth/signup',
  LOGIN: '/auth/login',
  LOG_OUT: '/auth/logout',
  VERIFY_ACCOUNT: '/auth/confirm-signup',
  FORGOT_PASSWORD: '/auth/forget-password',
  RESET_PASSWORD: '/auth/reset-password',
  CHANGE_PASSWORD: '/auth/change-password',
  RESEND_CODE: '/auth/signup/resend-code',
  TOKEN: '/auth/token',
  OAUTH_CALLBACK: (code: string) => `/auth/social/signin?code=${code}`,
  MFA_SETUP: '/auth/mfa/totp/setup',
  MFA_VERIFY: '/auth/mfa/totp/verify',
  MFA_DISABLE: '/auth/mfa/totp/disable',
  MFA_STATUS: '/auth/mfa/status',
  MFA_CHALLENGE: '/auth/mfa/challenge',
  MFA_RECOVERY_GENERATE: '/auth/mfa/recovery/generate',
  MFA_RECOVERY_VERIFY: '/auth/mfa/recovery/verify',
  MFA_RECOVERY_STATUS: '/auth/mfa/recovery/status',
}

export const ANALYTICS_ENDPOINTS: Analytics = {
  PLATFORM_STATS: '/v1/stats/public',
}

export const SYSTEM_ENDPOINTS: System = {
  VERSION: '/healthz',
}

export const CHAT_ENDPOINTS: Chat = {
  SUBMIT_CHAT: cfg.CHAT_MODE === 'agent' ? '/v1/chat/agent' : '/v1/chat',
  PROMPT_SUGGESTIONS: '/v1/prompts/suggestions',
  GET_CHAT_HISTORY: (sessionId: string) => `/v1/chat/${sessionId}/history`,
  GET_USER_CONVERSATIONS: (
    page: number,
    pageSize: number,
    startDate?: string,
    endDate?: string
  ): string => {
    const queryParams = [
      `page=${page}`,
      `pageSize=${pageSize}`,
      startDate && `start_date=${startDate}T00:00:00Z`,
      endDate && `end_date=${endDate}T23:59:59Z`,
    ]
      .filter(Boolean)
      .join('&')

    return `/v1/users/conversations?${queryParams}`
  },
  CREATE_WALK: (sessionId: string) => `/v1/chat/${sessionId}/walk`,
  REVOKE_WALK: (token: string) => `/v1/chat/walk/${token}`,
  VIEW_WALK: (token: string) => `/v1/walk/${token}`,
  STREAM_WALK: (token: string) => `/v1/walk/${token}/stream`,
  CREATE_CLIP: (messageId: string) => `/v1/chat/messages/${messageId}/clip`,
  VIEW_CLIP: (token: string) => `/v1/clip/${token}`,
  REVOKE_CLIP: (token: string) => `/v1/chat/clips/${token}`,
}

export const PULSE_ENDPOINTS: Pulse = {
  TODAY: '/v1/pulse/today',
}

export const AHEAD_ENDPOINTS: Ahead = {
  GET: '/v1/ahead',
  COMPLETE: (id: string) => `/v1/ahead/${id}/complete`,
  DISMISS: (id: string) => `/v1/ahead/${id}/dismiss`,
  RESTORE: (id: string) => `/v1/ahead/${id}/restore`,
  REFRESH: '/v1/ahead/refresh',
}

export const HANDOFF_ENDPOINTS: Handoff = {
  GET: '/v1/handoff',
  UPDATE: '/v1/handoff',
  SHARE: '/v1/handoff/share',
  VIEW: (token: string) => `/v1/handoff/share/${token}`,
  REVOKE: (token: string) => `/v1/handoff/share/${token}`,
}

export const GOALS_ENDPOINTS = {
  SUGGESTIONS: '/v1/goals/suggestions',
  CLARIFY: '/v1/goals/clarify',
  CREATE: '/v1/goals',
  LIST: '/v1/goals',
  DETAIL: (id: string) => `/v1/goals/${id}`,
  ACTION: (id: string) => `/v1/goals/${id}`,
  BRIEF_READ: (briefId: string) => `/v1/goals/briefs/${briefId}/read`,
  BRIEF_DISMISS: (briefId: string) => `/v1/goals/briefs/${briefId}/dismiss`,
} as const

export const USER_PROFILE_ENDPOINTS: USER = {
  UPDATE_PROFILE_PICTURE: '/v1/profiles/picture',
  DELETE_PROFILE_PICTURE: '/v1/profiles/picture',
  GET_PROFILE: '/v1/profiles',
  UPDATE_PROFILE: '/v1/profiles',
  SAVE_PROFILE: '/v1/profiles',
  DELETE_ALL_DATA: '/v1/users/data',
  MEDICAL_PROFILE: '/v1/profiles/medical',
}

interface LocationEndpoints {
  CREATE_LOCATION: string
  UPDATE_LOCATION: string
  LIST_USER_LOCATIONS: string
}

export const LOCATION_ENDPOINTS: LocationEndpoints = {
  CREATE_LOCATION: '/v1/locations',
  UPDATE_LOCATION: '/v1/locations',
  LIST_USER_LOCATIONS: '/v1/user/locations',
}

// ─── Corporate Wellness (Organisation) ───

interface OrgEndpoints {
  // ─── Public (no auth) ───
  SSO_CONFIG: (slug: string) => string
  SSO_LOGIN: (slug: string) => string
  SSO_LOGIN_PROVIDER: (slug: string, provider: string) => string
  DOMAIN_LOOKUP: (domain: string) => string

  // ─── Protected (Cognito JWT) ───
  ME: string
  MEMBERS: (slug: string) => string
  DEACTIVATE_MEMBER: (slug: string, userId: string) => string
  ACCEPT_INVITE: (token: string) => string

  // ─── Frontend-only (backend not yet implemented) ───
  DASHBOARD: (slug: string) => string
  INVITE: (slug: string) => string
  BULK_INVITE: (slug: string) => string
  CSV_INVITE: (slug: string) => string
  ENGAGEMENT: (slug: string) => string
  ENGAGEMENT_EXPORT: (slug: string) => string
  BILLING: (slug: string) => string
  SETTINGS: (slug: string) => string
}

export const ORG_ENDPOINTS: OrgEndpoints = {
  // ─── Public (no auth) ───
  // GET /v1/org/{slug}/sso/config → { sso_enabled, provider_type, org_name, logo_url }
  SSO_CONFIG: (slug: string) => `/v1/org/${slug}/sso/config`,
  // GET /v1/org/{slug}/login → 302 redirect to Cognito with identity_provider hint
  SSO_LOGIN: (slug: string) => `/v1/org/${slug}/login`,
  // GET /v1/org/{slug}/login?provider=google|microsoft → federated SSO via specific provider
  SSO_LOGIN_PROVIDER: (slug: string, provider: string) => `/v1/org/${slug}/login?provider=${provider}`,
  // GET /v1/org/lookup?domain=citi.com → check if email domain matches an org
  DOMAIN_LOOKUP: (domain: string) => `/v1/org/lookup?domain=${domain}`,

  // ─── Protected (Cognito JWT) ───
  // GET /v1/org/me → current user's org membership
  ME: '/v1/org/me',
  // GET /v1/org/{slug}/members → list members (HR admin)
  MEMBERS: (slug: string) => `/v1/org/${slug}/members`,
  // DELETE /v1/org/{slug}/members/{user_id} → deactivate member
  DEACTIVATE_MEMBER: (slug: string, userId: string) => `/v1/org/${slug}/members/${userId}`,
  // POST /v1/org/invite/{token}/accept → accept invite link
  ACCEPT_INVITE: (token: string) => `/v1/org/invite/${token}/accept`,

  // ─── Frontend-only (backend endpoints pending) ───
  DASHBOARD: (slug: string) => `/v1/org/${slug}/dashboard`,
  INVITE: (slug: string) => `/v1/org/${slug}/members/invite`,
  BULK_INVITE: (slug: string) => `/v1/org/${slug}/members/bulk`,
  CSV_INVITE: (slug: string) => `/v1/org/${slug}/members/csv`,
  ENGAGEMENT: (slug: string) => `/v1/org/${slug}/engagement`,
  ENGAGEMENT_EXPORT: (slug: string) => `/v1/org/${slug}/engagement/export`,
  BILLING: (slug: string) => `/v1/org/${slug}/billing`,
  SETTINGS: (slug: string) => `/v1/org/${slug}/settings`,
}

interface ConnectorEndpoints {
  NOTION_SIGNIN: (userId: string) => string
  NOTION_STATUS: string
  NOTION_DISCONNECT: string
  GOOGLE_DRIVE_SIGNIN: (userId: string) => string
  GOOGLE_DRIVE_STATUS: string
  GOOGLE_DRIVE_DISCONNECT: string
  OURA_RING_SIGNIN: (userId: string) => string
  OURA_RING_STATUS: string
  OURA_RING_DISCONNECT: string
  TERRA_AUTHENTICATE: string
  TERRA_CONNECT: string
  TERRA_STATUS: string
  AVAILABLE: string
  TERRA_DISCONNECT: string
  HEALTH_PORTAL_LIST: string
  HEALTH_PORTAL_CONNECT: string
  HEALTH_PORTAL_STATUS: string
  HEALTH_PORTAL_DISCONNECT: string
}

interface MemoryEndpoints {
  ERASE_ALL: string
  SETTINGS: string
  MEMORY_CONSENT: string
  PORTAL_CONSENT: string
}

export const MEMORY_ENDPOINTS: MemoryEndpoints = {
  ERASE_ALL: '/v1/memories',
  SETTINGS: '/v1/settings/consent',
  MEMORY_CONSENT: '/v1/settings/consent/memory',
  PORTAL_CONSENT: '/v1/settings/consent/portal',
}

export const CONNECTOR_ENDPOINTS: ConnectorEndpoints = {
  NOTION_SIGNIN: (userId: string) => `/auth/notion/signin/${userId}`,
  NOTION_STATUS: '/auth/notion/status',
  NOTION_DISCONNECT: '/v1/auth/notion/disconnect',
  GOOGLE_DRIVE_SIGNIN: (userId: string) => `/auth/google-drive/signin/${userId}`,
  GOOGLE_DRIVE_STATUS: '/auth/google-drive/status',
  GOOGLE_DRIVE_DISCONNECT: '/v1/auth/google-drive/disconnect',
  OURA_RING_SIGNIN: (userId: string) => `/auth/oura-ring/signin/${userId}`,
  OURA_RING_STATUS: '/auth/oura-ring/status',
  OURA_RING_DISCONNECT: '/v1/auth/oura-ring/disconnect',
  TERRA_AUTHENTICATE: '/v1/connectors/terra-health/authenticate',
  TERRA_CONNECT: '/v1/connectors/terra-health/connect',
  TERRA_STATUS: '/auth/terra-health/status',
  AVAILABLE: '/v1/connectors/available',
  TERRA_DISCONNECT: '/v1/auth/terra-health/disconnect',
  HEALTH_PORTAL_LIST: '/v1/connectors/health-portal/list',
  HEALTH_PORTAL_CONNECT: '/v1/connectors/health-portal/connect',
  HEALTH_PORTAL_STATUS: '/auth/health-portal/status',
  HEALTH_PORTAL_DISCONNECT: '/v1/auth/health-portal/disconnect',
}
